import { useCultureCode } from '@mews-commander/core';
import { getCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { useCommanderSelector } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { NumberService, useLocalizeCurrency, useLocalizePercentage } from '@mews/framework';
import { CancellationPolicyTimingProps, useCancellationPolicyTimingGetter } from './CancellationPolicyTiming';

interface CancellationPolicyNameRelatedProps extends CancellationPolicyTimingProps {
    absoluteFee?: number;
    absoluteFeeCurrencyCode?: string;
    name?: string | null | undefined;
    relativeFee: number;
}

type FormatConfig = 'name' | 'parameters' | 'nameAndParameters';

export const useCancellationPolicyNameGetter = () => {
    const localizeCurrency = useLocalizeCurrency();
    const localizePercent = useLocalizePercentage();
    const getCancellationPolicyTiming = useCancellationPolicyTimingGetter();

    const cultureCode = useCultureCode();
    const currencyCultureInfo = useCommanderSelector((state) => getCurrencyCultureInfo(state, { cultureCode }));

    return (
        props: CancellationPolicyNameRelatedProps,
        { format, nonBreakingSpaces }: { format?: FormatConfig; nonBreakingSpaces?: boolean } = {
            format: 'name',
            nonBreakingSpaces: true,
        }
    ) => {
        const timing = getCancellationPolicyTiming(props);
        const spaceChar = nonBreakingSpaces ? UnicodeEntity.NoBreakSpace : UnicodeEntity.Space;

        const absoluteFee =
            props.absoluteFee != null
                ? `${
                      props.absoluteFeeCurrencyCode
                          ? localizeCurrency(props.absoluteFee, props.absoluteFeeCurrencyCode)
                          : NumberService.formatCurrencyValue(props.absoluteFee, currencyCultureInfo, {
                                precision: 2,
                                showSymbol: false,
                            })
                  }`
                : '';
        const relativeFee =
            props.relativeFee != null ? `${localizePercent(props.relativeFee, { maximumFractionDigits: 2 })}` : '';

        const parameters = `${timing}${
            absoluteFee || relativeFee ? `${UnicodeEntity.Colon}${spaceChar}` : ''
        }${absoluteFee}${absoluteFee && relativeFee ? `${UnicodeEntity.Comma}${spaceChar}` : ''}${relativeFee}`;

        switch (format) {
            case 'parameters':
                return parameters;
            case 'nameAndParameters':
                return `${parameters}${
                    props.name
                        ? `${spaceChar}${UnicodeEntity.LeftParenthesis}${props.name}${UnicodeEntity.RightParenthesis}`
                        : ''
                }`;
            case 'name':
            default:
                return props.name || parameters;
        }
    };
};

interface CancellationPolicyNameProps extends CancellationPolicyNameRelatedProps {
    format?: FormatConfig;
}

export const CancellationPolicyName = ({ format, ...other }: CancellationPolicyNameProps) => {
    const getCancellationPolicyName = useCancellationPolicyNameGetter();
    return <span>{getCancellationPolicyName(other, { format })}</span>;
};
